import styled from 'styled-components';
import {
  ReactNode,
  ElementType,
  CSSProperties,
  useState,
  createContext,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import { theme } from 'antd';
import type { GlobalToken } from 'antd';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';

// TODO: revisit once the design system is properly implemented
const LIBERTIFY_GREEN_100 = '#ECFFFE'; // active bg + active hover bg
const LIBERTIFY_GREEN_700 = '#247F79'; // active text + icon
const MONO_200 = '#F5FAFA'; // idle hover bg

/**
 * Shared sidebar look, exported so app-side pieces rendered inside the
 * sidebar (user menu, popover items) match the lib's own rows.
 */
export const sidebarTokens = {
  /** 1px lines (below the header, footer divider, collapsed sections): secondary-300 at 10% opacity (0x1A). */
  dividerColor: `${colors.secondary[300]}1A`,
  /** Hover background of an idle row (items, footer user menu). */
  itemHoverBg: MONO_200,
  /** Section titles and secondary footer text (e.g. the plan name). Not in the palette yet. */
  subtleTextColor: '#B9BEC7',
  /** Background of a `tone="muted"` section (#9FA6B5 at 4% opacity, 0x0A). Not in the palette yet. */
  mutedSectionBg: '#9FA6B50A',
  /** Menu item typography. Color comes from the `colorText` token. */
  itemFont: {
    fontFamily: typography.fontFamily.secondary,
    fontSize: typography.body.small.fontSize,
    letterSpacing: '-0.01em',
  },
} as const;

export type SidebarWidth = 'compact' | 'default' | 'wide';

/** Sidebar widths in px per preset, exported so layouts can offset content by the real width. */
export const SIDEBAR_WIDTHS: Record<
  SidebarWidth,
  { expanded: number; collapsed: number }
> = {
  compact: { expanded: 200, collapsed: 56 },
  default: { expanded: 240, collapsed: 64 },
  wide: { expanded: 270, collapsed: 72 },
};

// Context for sidebar state
interface SidebarContextValue {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
  token: GlobalToken;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a Sidebar component');
  }
  return context;
};

export interface SidebarProps {
  /** Children elements */
  children?: ReactNode;
  /** Width variant */
  width?: SidebarWidth;
  /** Whether sidebar is collapsed */
  collapsed?: boolean;
  /** Callback when collapse state changes */
  onCollapseChange?: (collapsed: boolean) => void;
  /** Whether sidebar can be collapsed */
  collapsible?: boolean;
  /** Header content (usually logo) */
  header?: ReactNode;
  /** Top content */
  topContent?: ReactNode;
  /** Footer content (usually user menu) */
  footer?: ReactNode;
  /** Additional class name */
  className?: string;
  /** Test id for testing */
  'data-testid'?: string;
}

export interface SidebarItemProps {
  /** Label text */
  label: string;
  /** Icon element */
  icon?: ReactNode;
  /** Whether item is active */
  active?: boolean;
  /** Whether item is disabled */
  disabled?: boolean;
  /** Click handler */
  onClick?: () => void;
  /**
   * Element or component to render as. Defaults to `'button'`.
   * Pass a router `Link` (e.g. `as={Link} to="/path"`) to render a navigation
   * anchor; any extra props (`to`, `href`, …) are forwarded to it. The lib
   * stays router-agnostic — the consumer supplies the link component.
   */
  as?: ElementType;
  /** Additional class name */
  className?: string;
  /** Test id for testing */
  'data-testid'?: string;
}

export interface SidebarDividerProps {
  /** Additional class name */
  className?: string;
  /** Inline style (e.g. vertical spacing) */
  style?: CSSProperties;
}

export type SidebarSectionTone = 'default' | 'muted';

export interface SidebarSectionProps {
  /** Section title */
  title?: string;
  /**
   * 'muted' renders the section as a tinted block with a top line that
   * grows to fill the remaining menu height (meant for the last section).
   */
  tone?: SidebarSectionTone;
  /** Children items */
  children?: ReactNode;
  /** Additional class name */
  className?: string;
}

interface StyledSidebarProps {
  $token: GlobalToken;
  $width: number;
  $collapsed: boolean;
}

const StyledSidebar = styled.aside<StyledSidebarProps>`
  display: flex;
  flex-direction: column;
  width: ${({ $width }) => $width}px;
  min-width: ${({ $width }) => $width}px;
  height: 100%;
  background: ${({ $token }) => $token.colorBgContainer};
  border-right: ${({ $token }) =>
    `${$token.lineWidth}px solid ${$token.colorBorderSecondary}`};
  transition:
    width ${({ $token }) => $token.motionDurationMid} ease-in-out,
    min-width ${({ $token }) => $token.motionDurationMid} ease-in-out;
  overflow: hidden;
`;

/*
 * Every row (header, section titles, items, footer) shares one horizontal
 * inset: `paddingLG` when expanded, none when collapsed so icons can center
 * in the narrow rail.
 */
const rowPaddingX = (token: GlobalToken, collapsed: boolean): number =>
  collapsed ? 0 : token.paddingLG;

const SidebarHeader = styled.div<{ $token: GlobalToken; $collapsed: boolean }>`
  padding: ${({ $token, $collapsed }) =>
    `${$token.padding}px ${rowPaddingX($token, $collapsed)}px`};
  display: flex;
  align-items: center;
  min-height: 64px;
  border-bottom: ${({ $token }) =>
    `${$token.lineWidth}px solid ${sidebarTokens.dividerColor}`};
`;

const SidebarTopContent = styled.div`
  flex-shrink: 0;
`;

const SidebarContent = styled.div<{ $token: GlobalToken }>`
  flex: 1;
  overflow-y: auto;
  /* Column so a muted last section can grow down to the footer. */
  display: flex;
  flex-direction: column;
  /*
   * No horizontal padding: SidebarItem rows are full-width rectangles and
   * carry the single row inset themselves. The top inset keeps the first
   * row (section title or item) 24px below whatever sits above it.
   */
  padding: ${({ $token }) => `${$token.paddingLG}px 0 0`};
`;

/* No vertical padding: footer rows (e.g. a user menu with a hover state) own
   their vertical spacing so their background can reach the sidebar's edges. */
const SidebarFooter = styled.div<{ $token: GlobalToken; $collapsed: boolean }>`
  padding: ${({ $token, $collapsed }) =>
    `0 ${rowPaddingX($token, $collapsed)}px`};
  margin-top: auto;
`;

/* Bleeds out of the footer's row inset so the line spans the full width. */
const StyledSidebarDivider = styled.div<{
  $token: GlobalToken;
  $collapsed: boolean;
}>`
  height: ${({ $token }) => $token.lineWidth}px;
  background: ${sidebarTokens.dividerColor};
  margin: 0 -${({ $token, $collapsed }) => rowPaddingX($token, $collapsed)}px;
`;

interface StyledSidebarItemProps {
  $token: GlobalToken;
  $active: boolean;
  $disabled: boolean;
  $collapsed: boolean;
}

const getItemColor = (
  token: GlobalToken,
  disabled: boolean,
  active: boolean,
): string => {
  if (disabled) return token.colorTextDisabled;
  if (active) return LIBERTIFY_GREEN_700;
  return token.colorText;
};

const StyledSidebarItem = styled.button<StyledSidebarItemProps>`
  display: flex;
  align-items: center;
  gap: ${({ $token }) => $token.paddingXS}px;
  width: 100%;
  min-height: 44px;
  padding: ${({ $token, $collapsed }) =>
    `0 ${rowPaddingX($token, $collapsed)}px`};
  border: none;
  border-radius: 0;
  background: ${({ $active }) =>
    $active ? LIBERTIFY_GREEN_100 : 'transparent'};
  color: ${({ $active, $disabled, $token }) =>
    getItemColor($token, $disabled, $active)};
  font-family: ${sidebarTokens.itemFont.fontFamily};
  font-size: ${sidebarTokens.itemFont.fontSize}px;
  letter-spacing: ${sidebarTokens.itemFont.letterSpacing};
  font-weight: ${({ $active }) =>
    $active ? typography.fontWeight.semibold : typography.fontWeight.regular};
  text-align: left;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all ${({ $token }) => $token.motionDurationFast} ease-in-out;
  justify-content: ${({ $collapsed }) =>
    $collapsed ? 'center' : 'flex-start'};
  overflow: hidden;
  white-space: nowrap;

  &:hover {
    ${({ $disabled, $active, $token }) =>
      $disabled
        ? ''
        : `
      background: ${$active ? LIBERTIFY_GREEN_100 : MONO_200};
      color: ${getItemColor($token, $disabled, $active)};
    `}
  }

  &:focus-visible {
    outline: ${({ $token }) => $token.lineWidthBold}px solid
      ${({ $token }) => $token.colorPrimary};
    outline-offset: -${({ $token }) => $token.lineWidthBold}px;
  }

  svg {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
  }
`;

interface ItemLabelProps {
  $token: GlobalToken;
  $collapsed: boolean;
}

const ItemLabel = styled.span<ItemLabelProps>`
  opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
  transition: opacity ${({ $token }) => $token.motionDurationFast} ease-in-out;
  text-overflow: ellipsis;
  display: ${({ $collapsed }) => ($collapsed ? 'none' : 'inline')};
`;

const mutedSectionStyles = (token: GlobalToken, collapsed: boolean) => `
  flex: 1 0 auto;
  background: ${sidebarTokens.mutedSectionBg};
  border-top: ${token.lineWidth}px solid ${sidebarTokens.dividerColor};
  ${collapsed ? `padding-top: ${token.paddingXS}px;` : ''}
`;

const StyledSidebarSection = styled.div<{
  $token: GlobalToken;
  $collapsed: boolean;
  $tone: SidebarSectionTone;
}>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: ${({ $token }) => $token.marginXS}px;

  &:last-child {
    margin-bottom: 0;
  }

  ${({ $tone, $token, $collapsed }) =>
    $tone === 'muted' ? mutedSectionStyles($token, $collapsed) : ''}

  ${({ $collapsed, $tone }) =>
    $collapsed
      ? $tone === 'muted'
        ? '' /* the muted block's top line already separates it */
        : `
    &:not(:first-of-type)::before {
      content: '';
      display: block;
      width: 34px;
      height: 1px;
      background: ${sidebarTokens.dividerColor};
      margin: 8px auto;
    }
  `
      : `
    &:not(:first-of-type) {
      margin-top: 30px;
    }
  `}
`;

interface SectionTitleProps {
  $token: GlobalToken;
  $collapsed: boolean;
}

const SectionTitle = styled.div<SectionTitleProps>`
  padding: ${({ $token }) =>
    `${$token.paddingLG}px ${$token.paddingLG}px ${$token.paddingXXS}px`};
  color: ${sidebarTokens.subtleTextColor};
  font-family: ${typography.fontFamily.secondary};
  font-size: 12px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  display: ${({ $collapsed }) => ($collapsed ? 'none' : 'block')};
`;

const CollapseButton = styled.button<{ $token: GlobalToken }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $token }) => $token.controlHeightSM}px;
  height: ${({ $token }) => $token.controlHeightSM}px;
  border: ${({ $token }) =>
    `${$token.lineWidth}px solid ${$token.colorBorderSecondary}`};
  border-radius: ${({ $token }) => $token.borderRadius}px;
  background: ${({ $token }) => $token.colorBgContainer};
  color: ${({ $token }) => $token.colorTextSecondary};
  cursor: pointer;
  transition: all ${({ $token }) => $token.motionDurationFast} ease-in-out;
  margin-left: auto;

  &:hover {
    background: ${({ $token }) => $token.colorBgLayout};
  }
`;

/**
 * Sidebar component based on Libertify Design System
 *
 * Now uses Ant Design theme tokens for consistent styling.
 *
 * @example
 * ```tsx
 * import { Sidebar, SidebarItem, SidebarSection } from '@libertify/ui';
 *
 * <Sidebar
 *   header={<Logo />}
 *   footer={<UserMenu />}
 *   collapsible
 * >
 *   <SidebarSection title="Main">
 *     <SidebarItem label="Dashboard" icon={<HomeIcon />} active />
 *     <SidebarItem label="Projects" icon={<FolderIcon />} />
 *   </SidebarSection>
 * </Sidebar>
 * ```
 */
export const Sidebar = ({
  children,
  width = 'default',
  collapsed: controlledCollapsed,
  onCollapseChange,
  collapsible = false,
  header,
  topContent,
  footer,
  className,
  'data-testid': testId,
}: SidebarProps) => {
  const { token } = theme.useToken();
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const isControlled = controlledCollapsed !== undefined;
  const collapsed = isControlled ? controlledCollapsed : internalCollapsed;

  const handleCollapseChange = useCallback(
    (value: boolean) => {
      if (!isControlled) {
        setInternalCollapsed(value);
      }
      onCollapseChange?.(value);
    },
    [isControlled, onCollapseChange],
  );

  const currentWidth = collapsed
    ? SIDEBAR_WIDTHS[width].collapsed
    : SIDEBAR_WIDTHS[width].expanded;

  const contextValue = useMemo(
    () => ({ collapsed, setCollapsed: handleCollapseChange, token }),
    [collapsed, handleCollapseChange, token],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <StyledSidebar
        $token={token}
        $width={currentWidth}
        $collapsed={collapsed}
        className={className}
        data-testid={testId}
      >
        {header && (
          <SidebarHeader $token={token} $collapsed={collapsed}>
            {header}
            {collapsible && (
              <CollapseButton
                $token={token}
                onClick={() => handleCollapseChange(!collapsed)}
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  style={{
                    transform: collapsed ? 'rotate(180deg)' : 'none',
                    transition: `transform ${token.motionDurationMid}`,
                  }}
                >
                  <path
                    d="M10 12L6 8L10 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </CollapseButton>
            )}
          </SidebarHeader>
        )}
        {topContent && <SidebarTopContent>{topContent}</SidebarTopContent>}
        <SidebarContent $token={token}>{children}</SidebarContent>
        {footer && (
          <SidebarFooter $token={token} $collapsed={collapsed}>
            {footer}
          </SidebarFooter>
        )}
      </StyledSidebar>
    </SidebarContext.Provider>
  );
};

/**
 * SidebarItem component - navigation item within Sidebar
 *
 * Now uses Ant Design theme tokens for consistent styling.
 */
export const SidebarItem = ({
  label,
  icon,
  active = false,
  disabled = false,
  onClick,
  as,
  className,
  'data-testid': testId,
  ...rest
}: SidebarItemProps & Record<string, unknown>) => {
  const { collapsed, token } = useSidebar();

  // `disabled` is only a valid native attribute on <button>; for custom
  // elements (e.g. a router Link) fall back to aria-disabled.
  const isButton = as === undefined;

  return (
    <StyledSidebarItem
      as={as}
      $token={token}
      $active={active}
      $disabled={disabled}
      $collapsed={collapsed}
      disabled={isButton ? disabled : undefined}
      aria-disabled={disabled || undefined}
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
      className={className}
      data-testid={testId}
      title={collapsed ? label : undefined}
      {...rest}
    >
      {icon}
      <ItemLabel $token={token} $collapsed={collapsed}>
        {label}
      </ItemLabel>
    </StyledSidebarItem>
  );
};

/**
 * SidebarDivider - a full-width 1px line for the sidebar footer (or any
 * other row area), in the sidebar's divider color. It cancels the row
 * inset so it always spans edge to edge.
 */
export const SidebarDivider = ({ className, style }: SidebarDividerProps) => {
  const { collapsed, token } = useSidebar();

  return (
    <StyledSidebarDivider
      role="separator"
      $token={token}
      $collapsed={collapsed}
      className={className}
      style={style}
    />
  );
};

/**
 * SidebarSection component - groups items with optional title
 *
 * Now uses Ant Design theme tokens for consistent styling.
 */
export const SidebarSection = ({
  title,
  tone = 'default',
  children,
  className,
}: SidebarSectionProps) => {
  const { collapsed, token } = useSidebar();

  return (
    <StyledSidebarSection
      $token={token}
      $collapsed={collapsed}
      $tone={tone}
      className={className}
    >
      {title && (
        <SectionTitle $token={token} $collapsed={collapsed}>
          {title}
        </SectionTitle>
      )}
      {children}
    </StyledSidebarSection>
  );
};

export default Sidebar;
